import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import PageTransition from '../components/layout/PageTransition';
import TabContainer from '../components/layout/TabContainer';
import { useAuth } from '../contexts/AuthContext';
import { USER_ROLES } from '@watchlist/shared';
import SplashScreen from '../components/pages/SplashScreen';
import LoginScreen from '../components/pages/LoginScreen';
import AppLayout from '../components/layout/AppLayout';
import OnboardingWrapper from '../components/pages/OnboardingWrapper';
import {
  AccessPendingScreen,
  AccessDeniedScreen,
} from '../components/access-control';
import { AdminConsole, ModeratorConsole } from '../components/admin';
import {
  WatchlistDetailRoute,
  MovieDetailRoute,
  SearchPageRoute,
  ProfilePageRoute,
  OtherUserProfileRoute,
  SettingsScreenRoute,
  UsersListRoute,
  NotificationsRoute,
  PersonDetailRoute,
  PersonCreditsRoute,
  TrendingPageRoute,
  TrendingTabRoute,
  RecentlyAddedPageRoute,
  AllReviewsPageRoute,
  UserReviewsPageRoute,
  ReviewDetailRoute,
  UserWatchedPageRoute,
  OtherUserWatchlistsPageRoute,
  FavoriteStarsPageRoute,
  CrewRoute,
  FindCrewRoute,
  HiddenFromCrewRoute,
  DataDeletionRoute,
  StoryViewerRoute,
  // MarkAsWatchedPageRoute - DEPRECATED: Using MarkAsWatchedDialog instead
} from './RouteComponents';
import NotFoundScreen from '../components/pages/NotFoundScreen';
import CardPreviewPage from '../components/pages/CardPreviewPage';
import MovieHeroPreviewPage from '../components/pages/MovieHeroPreviewPage';
import { isSharedRoute } from './sharedRouteAccess';

const CrewHomeRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/${location.search}`} replace />;
};

const UserWantToWatchRedirect: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  return <Navigate to={`/profile/${userId}/watched?tab=want`} replace />;
};

const AppRoutes: React.FC = () => {
  const { user, isLoading, isRoleLoading, handleLoginSuccess, handleLogout, userRole, accessRequestStatus, isAdmin, canModerateCrew, refreshUserRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // handleLogout (from AuthContext) now handles native sign-out internally
  const handleSignOut = () => handleLogout();

  // Show splash screen while checking authentication or while role is loading
  if (isLoading || isRoleLoading) {
    return <SplashScreen showSpinner />;
  }

  // Check if this is a shared link that should bypass login
  const isSharedLink = isSharedRoute(location.pathname, location.search);

  // Show login screen when not authenticated (unless it's a shared link)
  if (!user && !isSharedLink) {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  // ============================================
  // ACCESS CONTROL: Handle pending users
  // ============================================
  if (user && userRole === USER_ROLES.PENDING) {
    // User's request was rejected
    if (accessRequestStatus === 'rejected') {
      return (
        <AccessDeniedScreen
          user={user}
          onLogout={handleSignOut}
        />
      );
    }

    // User is pending approval (access request auto-created on signup)
    // Show pending screen for both 'none' and 'pending' status
    // 'none' status means the auto-create is still in progress or just completed
    return (
      <AccessPendingScreen
        user={user}
        onLogout={handleSignOut}
        onAccessGranted={refreshUserRole}
      />
    );
  }

  // For shared links without authentication, render without OnboardingWrapper
  if (!user && isSharedLink) {
    return (
      <AppLayout>
        <Routes>
          {/* Shared content routes - accessible without login */}
          <Route path="/movie/:movieId" element={<MovieDetailRoute />} />
          <Route path="/open/movie/:movieId" element={<MovieDetailRoute />} />
          <Route path="/watchlist/:watchlistId" element={<WatchlistDetailRoute />} />
          <Route path="/profile/:userId" element={<OtherUserProfileRoute />} />
          <Route path="/person/:personId" element={<PersonDetailRoute />} />
          <Route path="/data-deletion" element={<DataDeletionRoute />} />

          {/* Catch all - redirect to login for non-shared routes */}
          <Route path="*" element={<LoginScreen onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      </AppLayout>
    );
  }

  // ============================================
  // ADMIN ROUTE: Separate from main app layout
  // ============================================
  if (location.pathname === '/admin') {
    if (!isAdmin) {
      // Non-admin trying to access admin console - redirect to home
      return <Navigate to="/" replace />;
    }
    return <AdminConsole user={user!} onBack={() => navigate(-1)} />;
  }

  if (location.pathname === '/moderator') {
    if (!canModerateCrew) {
      return <Navigate to="/" replace />;
    }
    return <ModeratorConsole user={user!} onBack={() => navigate(-1)} />;
  }

  // Tab paths kept alive — components stay mounted across navigation so state,
  // scroll position, and in-flight fetches are preserved exactly like a native
  // tab bar. Show/hide is driven by the real location (not the deferred
  // displayLocation inside PageTransition) so the correct tab is visible
  // immediately. Routes below returns null for these paths so React never
  // mounts a second copy while the keep-alive version is already showing.
  const tabPath = location.pathname;

  // Show main app content when authenticated
  return (
    <OnboardingWrapper user={user!}>
      <AppLayout>
        {/* ── Keep-alive tab pages ───────────────────────────────────────── */}
        {/* Each TabContainer is a position:fixed; overflow:auto element, so the
            browser preserves each tab's scrollTop natively across display:none/
            block toggles. No save/restore code needed for tab switches. */}
        <TabContainer visible={tabPath === '/'}>
          <CrewRoute />
        </TabContainer>
        <TabContainer visible={tabPath === '/search'}>
          <SearchPageRoute />
        </TabContainer>
        <TabContainer visible={tabPath === '/trending'}>
          <TrendingTabRoute />
        </TabContainer>
        <TabContainer visible={tabPath === '/notifications'}>
          <NotificationsRoute />
        </TabContainer>
        <TabContainer visible={tabPath === '/profile'}>
          <ProfilePageRoute />
        </TabContainer>

        {/* ── Non-tab routes (push/pop navigation) ──────────────────────── */}
        <PageTransition>
          {(displayLocation) => (
          <Routes location={displayLocation}>
          {/* Tab routes handled by keep-alive above — return null so Routes
              never mounts a duplicate while the kept-alive copy is visible. */}
          <Route path="/" element={null} />
          <Route path="/search" element={null} />
          <Route path="/trending" element={null} />
          <Route path="/notifications" element={null} />
          <Route path="/profile" element={null} />

          {/* Shared content entry points (avoids Hosting → Cloud Function loops for browsers) */}
          <Route path="/open/movie/:movieId" element={<MovieDetailRoute />} />
          <Route path="/open/review/:reviewId" element={<ReviewDetailRoute />} />
          <Route path="/open/person/:personId" element={<PersonDetailRoute />} />
          <Route path="/open/profile/:userId" element={<OtherUserProfileRoute />} />
          <Route path="/open/watchlist/:watchlistId" element={<WatchlistDetailRoute />} />

          {/* Review detail deep link */}
          <Route path="/review/:reviewId" element={<ReviewDetailRoute />} />

          {/* Recently Added page */}
          <Route path="/recently-added" element={<RecentlyAddedPageRoute />} />

          {/* Watchlist routes */}
          <Route path="/watchlist/:watchlistId" element={<WatchlistDetailRoute />} />

          {/* Movie routes */}
          <Route path="/movie/:movieId" element={<MovieDetailRoute />} />
          <Route path="/movie/:movieId/reviews" element={<AllReviewsPageRoute />} />
          {/* DEPRECATED: MarkAsWatchedPage route - Using MarkAsWatchedDialog instead */}
          {/* <Route path="/movie/:movieId/review" element={<MarkAsWatchedPageRoute />} /> */}

          {/* Person routes */}
          <Route path="/person/:personId" element={<PersonDetailRoute />} />
          <Route path="/person/:personId/credits" element={<PersonCreditsRoute />} />

          {/* Profile sub-routes (not the tab root — that's keep-alive above) */}
          <Route path="/profile/reviews" element={<UserReviewsPageRoute />} />
          <Route path="/profile/watched" element={<UserWatchedPageRoute />} />
          <Route path="/profile/want-to-watch" element={<Navigate to="/profile/watched?tab=want" replace />} />
          <Route path="/profile/favorite-stars" element={<FavoriteStarsPageRoute />} />
          <Route path="/profile/:userId" element={<OtherUserProfileRoute />} />
          <Route path="/profile/:userId/reviews" element={<UserReviewsPageRoute />} />
          <Route path="/profile/:userId/watched" element={<UserWatchedPageRoute />} />
          <Route path="/profile/:userId/want-to-watch" element={<UserWantToWatchRedirect />} />
          <Route path="/profile/:userId/watchlists" element={<OtherUserWatchlistsPageRoute />} />
          <Route path="/profile/:userId/favorite-stars" element={<FavoriteStarsPageRoute />} />

          {/* Settings & data rights (store URL: /data-deletion) */}
          <Route path="/settings" element={<SettingsScreenRoute />} />
          <Route path="/data-deletion" element={<DataDeletionRoute />} />

          {/* Users List */}
          <Route path="/users" element={<UsersListRoute />} />

          {/* Stories */}
          <Route path="/stories/:authorUid" element={<StoryViewerRoute />} />

          {/* Crew sub-pages; /crew itself redirects to home, preserving query params */}
          <Route path="/crew/find" element={<FindCrewRoute />} />
          <Route path="/crew/hidden" element={<HiddenFromCrewRoute />} />
          <Route path="/crew" element={<CrewHomeRedirect />} />

          {/* Dev-only design preview */}
          <Route path="/dev/card-preview" element={<CardPreviewPage />} />
          <Route path="/dev/movie-hero-preview" element={<MovieHeroPreviewPage />} />

          {/* Admin route handled above */}
          <Route path="/admin" element={<Navigate to="/admin" replace />} />

          {/* Catch all - show 404 page */}
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
          )}
        </PageTransition>
      </AppLayout>
    </OnboardingWrapper>
  );
};

export default AppRoutes; 